<template>
  <div class="publishing-mp-config">
    <DmsLimitReachedBanner v-if="!originalMpStatus" class="banner" />
    <InstantlyPublishedBanner
      class="banner"
      :composition="composition"
      :relevant-pos-types="[posStatus.posType]"
    />

    <ConfigList>
      <ul>
        <li>
          <MarketplaceLogo class="logo" />
          <ToggleSwitch
            :model-value="composition.publishingDetails.d2c.mp"
            :disabled="toggleDisabled"
            @update:model-value="setMpPublishing"
          />
        </li>
        <li>
          <p>{{ $t('PUBLISHING.DETAIL.POS.STATUS.LABEL') }}</p>
          <div class="publishing-status">
            <CompositionPosStatusBadge
              :pos-types="[posStatus.posType]"
              :status="posStatus.status"
              hide-icon
            />
            <InstantlyPublishedHint
              :composition="composition"
              :relevant-pos-types="[posStatus.posType]"
            />
          </div>
        </li>
        <li v-if="rejected">
          <p>{{ $t('PUBLISHING.DETAIL.POS.REJECTION_REASON.LABEL') }}</p>
          <button
            class="link rejection-reason-link"
            @click="openRejectionReasonDialog"
          >
            {{ $t(rejectionReasonName) }} <Icon icon="right" />
          </button>
        </li>
        <li v-if="online">
          <p>{{ $t('PUBLISHING.DETAIL.POS.VISIT.LABEL') }}</p>
          <div>
            <button
              class="link link-main icon-link icon-right"
              @click="openMpPreviewDialog"
            >
              {{ $t('PUBLISHING.DETAIL.POS.VISIT.CTA') }}
              <Icon icon="outside" />
            </button>
          </div>
        </li>
      </ul>
    </ConfigList>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { mapState, mapActions } from 'pinia';
import ToggleSwitch from 'commons/toggleSwitch/ToggleSwitch.vue';
import MarketplaceLogo from 'commons/logos/MarketplaceLogo.vue';
import ConfigList from 'src/app/components/publishing/ConfigList.vue';
import RejectionReasonDialog from 'src/app/components/publishing/RejectionReasonDialog.vue';
import PublishingMpPreviewDialog from './pos/mp/PublishingMpPreviewDialog.vue';
import dialogService from 'commons/dialogs/wrapper/dialogService';
import { rejectionReasonName } from 'commons/api/models/publishingCore/publishingCore';
import {
  getCompositionPosStateForPosType,
  COMPOSITION_POS_TYPES,
  COMPOSITION_POS_STATES,
} from 'commons/api/models/publishingCore/compositionPosStates';
import CompositionPosStatusBadge from 'src/app/components/publishing/statusBadges/CompositionPosStatusBadge.vue';
import DmsLimitReachedBanner from 'src/app/components/dms/DmsLimitReachedBanner.vue';
import InstantlyPublishedHint from 'src/app/components/publishing/statusBadges/InstantlyPublishedHint.vue';
import InstantlyPublishedBanner from 'src/app/components/publishing/InstantlyPublishedBanner.vue';
import { useCompositionStore } from 'commons/stores/composition';

export default {
  name: 'PublishingMpConfig',
  components: {
    InstantlyPublishedBanner,
    InstantlyPublishedHint,
    ToggleSwitch,
    MarketplaceLogo,
    ConfigList,
    CompositionPosStatusBadge,
    DmsLimitReachedBanner,
  },
  props: {},
  data() {
    return {
      mpSelected: false,
    };
  },
  computed: {
    ...mapState(useCompositionStore, {
      composition: (store) => store.current,
      originalMpStatus: (store) => store.original.publishingDetails.d2c.mp,
    }),
    ...mapGetters({
      publishingLimitReached: 'dms/publishingLimitReached',
    }),
    rejected() {
      return this.posStatus.status === COMPOSITION_POS_STATES.REJECTED;
    },
    online() {
      return this.posStatus.status === COMPOSITION_POS_STATES.PUBLISHED;
    },
    rejectionReasonName() {
      return rejectionReasonName(
        this.composition.publishingDetails.d2c.mpAccreditation
          .rejectionReasons?.[0],
      );
    },
    posStatus() {
      return getCompositionPosStateForPosType({
        composition: this.composition,
        posType: COMPOSITION_POS_TYPES.MP,
      });
    },
    toggleDisabled() {
      const toggleDisabledDueToRejection =
        this.rejected && !this.composition.publishingDetails.d2c.mp;
      return (
        (this.publishingLimitReached && !this.originalMpStatus) ||
        toggleDisabledDueToRejection
      );
    },
  },
  created() {},
  methods: {
    ...mapActions(useCompositionStore, ['setMpPublishing']),
    openRejectionReasonDialog() {
      dialogService.openDialog(RejectionReasonDialog, {
        reason:
          this.composition.publishingDetails.d2c.mpAccreditation
            .rejectionReasons?.[0],
      });
    },
    openMpPreviewDialog() {
      dialogService.openDialog(PublishingMpPreviewDialog);
    },
  },
};
</script>

<style lang="scss" scoped>
@import 'src/scss/styleguide/colors';

.logo {
  height: 24px;
}

.rejection-reason-link {
  font-weight: normal;
  color: $grey80;
  .icon {
    margin-left: 8px;
  }
}

.banner {
  margin-bottom: 24px;
}

.publishing-status {
  gap: 4px;
}
</style>
